import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { CARD_ASSETS, LEGENDARY_CARD_ART, CHAOS_ART } from "../../lib/assets";
import { primaryCTA, primaryCTATarget, CONFIG } from "../../lib/config";

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const handlePrimary = () => {
  const t = primaryCTATarget();
  if (t.startsWith("#")) scrollTo(t);
  else window.location.assign(t);
};

export const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative navy-bg grain overflow-hidden pt-32 md:pt-40 pb-10 md:pb-24"
    >
      {/* floating confetti dots */}
      <div className="absolute inset-0 pointer-events-none opacity-70">
        {[
          { left: "6%",  top: "20%", color: "var(--pw-yellow)" },
          { left: "12%", top: "70%", color: "var(--pw-orange)" },
          { left: "88%", top: "18%", color: "var(--pw-pink)"   },
          { left: "82%", top: "78%", color: "var(--pw-green)"  },
          { left: "48%", top: "10%", color: "#ffffff" },
          { left: "22%", top: "88%", color: "var(--pw-purple)" },
        ].map((d, i) => (
          <span
            key={i}
            className="absolute w-2 h-2 rounded-full"
            style={{ left: d.left, top: d.top, backgroundColor: d.color, opacity: 0.7 }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <div className="lg:col-span-7 text-white">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-display font-black leading-none tracking-tighter mb-3 md:mb-4"
              style={{
                color: "#fff",
                fontSize: "clamp(2rem, 6vw, 4rem)",
              }}
              data-testid="hero-brand"
            >
              People Watching
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display font-black leading-[0.86] tracking-tighter"
              data-testid="hero-headline"
              style={{ fontSize: "clamp(2.75rem, 8.5vw, 6.75rem)" }}
            >
              <span style={{ color: "var(--pw-green)" }}>Spot.</span>{" "}
              <span style={{ color: "var(--pw-pink)" }}>Pass.</span>{" "}
              <span style={{ color: "var(--pw-yellow)" }}>Win.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 md:mt-7 text-lg md:text-xl text-white/80 max-w-xl leading-relaxed"
              data-testid="hero-subheadline"
            >
              {CONFIG.brand.proposition}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 md:mt-9 flex flex-wrap items-center gap-3 md:gap-4"
            >
              <button
                data-testid="hero-primary-cta"
                onClick={handlePrimary}
                className="pw-btn-primary"
              >
                {primaryCTA()} <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid="hero-how-it-works"
                onClick={() => scrollTo("#how-it-works")}
                className="pw-btn-outline"
              >
                How It Works
              </button>
            </motion.div>

            {CONFIG.launch.isPreLaunch && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mt-6 text-sm text-white/55"
                data-testid="hero-launch-note"
              >
                Launching soon. Be first to play.
              </motion.p>
            )}
          </div>

          {/*
            HERO VISUAL — framed placeholder panel.
            Uses real production card artwork (Legendary + Trophy + Chaos) in a
            fanned composition inside a rounded, subtly warm panel so the
            section's navy background doesn't leak around the cards. When final
            product photography is supplied, replace the three <motion.img>
            inside the panel — the surrounding layout does not need to change.
          */}
          <div
            data-testid="hero-visual"
            className="lg:col-span-5 mt-4 lg:mt-0"
          >
            <div
              className="relative w-full overflow-hidden rounded-3xl border border-white/10 aspect-[4/2.7] sm:aspect-[4/3.2] md:aspect-[4/3.6] p-4 sm:p-6"
              style={{
                background:
                  "radial-gradient(ellipse at 25% 25%, rgba(245,197,66,0.18), transparent 55%), radial-gradient(ellipse at 78% 78%, rgba(232,106,44,0.18), transparent 55%), var(--pw-navy-2)",
              }}
            >
              {/* Confetti dots for playful texture */}
              <span className="absolute top-4 right-8 w-1.5 h-1.5 rounded-full" style={{ background: "var(--pw-pink)" }} />
              <span className="absolute top-10 right-16 w-1 h-1 rounded-full" style={{ background: "var(--pw-yellow)" }} />
              <span className="absolute bottom-10 left-6 w-1.5 h-1.5 rounded-full" style={{ background: "var(--pw-green)" }} />
              <span className="absolute bottom-4 left-16 w-1 h-1 rounded-full" style={{ background: "var(--pw-orange)" }} />

              <motion.div
                initial={{ opacity: 0, y: 30, rotate: -10 }}
                animate={{ opacity: 1, y: 0, rotate: -10 }}
                transition={{ duration: 0.9, delay: 0.2 }}
                data-testid="hero-card-1-wrap"
                className="absolute w-[52%] md:w-[46%] left-[4%] top-[10%] rounded-xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(0,0,0,0.65)] animate-float3 aspect-[3/4]"
              >
                <img
                  src={LEGENDARY_CARD_ART}
                  alt="Legendary Fancy Dress card artwork"
                  data-testid="hero-card-1"
                  className="absolute inset-0 w-full h-full object-cover scale-[1.18]"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30, rotate: 8 }}
                animate={{ opacity: 1, y: 0, rotate: 8 }}
                transition={{ duration: 0.9, delay: 0.35 }}
                data-testid="hero-card-2-wrap"
                className="absolute w-[50%] md:w-[44%] right-[3%] top-[6%] rounded-xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(0,0,0,0.65)] animate-float2 aspect-[3/4]"
              >
                <img
                  src={CARD_ASSETS.trophy}
                  alt="Trophy card artwork"
                  data-testid="hero-card-2"
                  className="absolute inset-0 w-full h-full object-cover scale-[1.18]"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30, rotate: -2 }}
                animate={{ opacity: 1, y: 0, rotate: -2 }}
                transition={{ duration: 0.9, delay: 0.5 }}
                data-testid="hero-card-3-wrap"
                className="absolute w-[48%] md:w-[42%] left-[26%] bottom-[6%] rounded-xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(0,0,0,0.65)] animate-floaty z-[2] aspect-[3/4]"
              >
                <img
                  src={CHAOS_ART.swapHands}
                  alt="Swap Hands chaos card artwork"
                  data-testid="hero-card-3"
                  className="absolute inset-0 w-full h-full object-cover scale-[1.18]"
                />
              </motion.div>

              <span
                data-testid="hero-visual-placeholder-badge"
                className="absolute bottom-3 right-3 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full font-semibold z-10"
                style={{ backgroundColor: "rgba(0,0,0,0.35)", color: "rgba(255,255,255,0.85)", backdropFilter: "blur(4px)" }}
              >
                Placeholder · Final photography to follow
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
